import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Milk, Loader2, Eye, EyeOff } from "lucide-react";
import Footer from "@/components/Footer";

const BG = "https://images.unsplash.com/photo-1500595046743-cd271d694d30?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Login() {
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password, remember);
      navigate(u.role === "super_admin" ? "/platform" : "/app", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const quickFill = (em, pw) => { setEmail(em); setPassword(pw); };
  void quickFill;

  return (
    <div className="flex min-h-screen flex-col">
    <div className="grid flex-1 lg:grid-cols-2">
      {/* Visual side */}
      <div className="relative hidden lg:block">
        <img src={BG} alt="Dairy farm at sunrise" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F1110]/90 via-[#1a2b1e]/70 to-[#3A5A40]/50" />
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")" }} />
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/15 backdrop-blur">
              <Milk className="h-5 w-5" />
            </div>
            <span className="font-heading text-lg font-bold">MilkERP</span>
          </div>
          <div className="max-w-md space-y-4">
            <h1 className="font-heading text-4xl font-extrabold leading-tight tracking-tight">
              {t("login.heroTitle")}
            </h1>
            <p className="text-white/75">
              {t("login.heroSubtitle")}
            </p>
          </div>
          <p className="text-xs text-white/50">© {new Date().getFullYear()} MilkERP — Multi-Tenant SaaS</p>
        </div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center bg-background p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2.5 lg:hidden">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Milk className="h-5 w-5" />
            </div>
            <span className="font-heading text-lg font-bold">MilkERP</span>
          </div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">{t("login.welcome")}</h2>
          <p className="mb-6 mt-1 text-sm text-muted-foreground">{t("login.subtitle")}</p>

          {error && (
            <div className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">{t("login.email")}</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@center.com" data-testid="login-email-input" autoComplete="email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">{t("login.password")}</Label>
              <div className="relative">
                <Input id="password" type={show ? "text" : "password"} required value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                  data-testid="login-password-input" autoComplete="current-password" />
                <button type="button" onClick={() => setShow((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-label="Toggle password">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <Checkbox checked={remember} onCheckedChange={setRemember} data-testid="remember-checkbox" /> {t("login.remember")}
              </label>
              <Link to="/forgot-password" className="text-sm font-medium text-primary hover:underline" data-testid="forgot-link">
                {t("login.forgot")}
              </Link>
            </div>
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {t("login.signIn")}
            </Button>
          </form>
        </div>
      </div>
    </div>
    <Footer />
    </div>
  );
}
